import {
  EscHotKeyIcon,
  ReturnHotKeyIcon,
} from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NAV_PATHS } from '@modules/common/constants';
import { useUpdateWisdomChat } from '@modules/oracle/hooks';
import { trackWisdomEvents } from '@modules/oracle/utils';
import { ArchiveRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { Chat, Status } from 'generated/graphql';
import { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

interface WisdomChatArchiveModalContentParams {
  chat: Omit<Chat, 'responses'>;
}

export function WisdomChatArchiveModalContent({
  chat,
}: WisdomChatArchiveModalContentParams) {
  const navigate = useNavigate();
  const { showSuccessNotification } = useNotificationContext();
  const { closeModal } = useMuiModalContext();

  const [updateWisdomChat, { loading: isUpdatingWisdomChat }] =
    useUpdateWisdomChat({
      onCompleted: () => {
        showSuccessNotification({
          contentComponent: `Wisdom chat "${chat.title}" archived successfully!`,
          props: {},
        });
        closeModal();
      },
    });

  const archiveWisdomChat = async (e: FormEvent) => {
    e.preventDefault();
    await updateWisdomChat({
      editToken: chat.editToken ?? '',
      status: Status.Archived,
    });

    try {
      trackWisdomEvents({
        eventName: 'Wisdom - Chat Archived',
        chatName: chat.title,
      });
    } catch {}

    // If the user is currently viewing this chat, reset to empty state
    // Used document.location.pathname as MUIModalContextProvider is outside of the Routes
    if (document.location.pathname.includes(chat.viewToken)) {
      navigate(`/${NAV_PATHS.WISDOM}`);
    }
  };

  return (
    <Box component="form" onSubmit={archiveWisdomChat}>
      <ModalHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <ArchiveRounded color="primary" />
            <Typography variant="h6">Archive Wisdom Chat</Typography>
          </Stack>
        }
      />
      <DialogContent dividers>
        <Typography>
          Are you sure you want to archive "{chat.title}"?
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => closeModal()}
          endIcon={<EscHotKeyIcon />}
        >
          No, Keep It
        </Button>
        <LoadingButton
          autoFocus
          variant="contained"
          type="submit"
          color="error"
          endIcon={<ReturnHotKeyIcon backgroundColor="error" />}
          loading={isUpdatingWisdomChat}
        >
          Yes, Archive
        </LoadingButton>
      </DialogActions>
    </Box>
  );
}
